import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminSubscriptionDetail } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface SubscriberState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data: AdminSubscriptionDetail | null;
  error: ApiError | null;
  reload: () => void;
  /**
   * Refetches without the loading state (TMMRSL-0056): the open detail stays mounted.
   * On failure the current data is kept and this rejects. A late answer is applied only
   * when it is still the latest request for the subscription now open.
   */
  refresh: () => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

function take(seq: Map<number, number>, forId: number): number {
  const token = (seq.get(forId) ?? 0) + 1;
  seq.set(forId, token);
  return token;
}

/** Loads one subscription detail by id. Idle when no id. */
export function useSubscriber(id: number | undefined): SubscriberState {
  const [status, setStatus] = useState<SubscriberState['status']>(id === undefined ? 'idle' : 'loading');
  const [data, setData] = useState<AdminSubscriptionDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const api = useTenantApi();
  const seq = useRef(new Map<number, number>());
  const idRef = useRef(id);

  const load = useCallback(
    (forId: number, quiet: boolean) => {
      const token = take(seq.current, forId);
      if (!quiet) {
        setStatus('loading');
        setError(null);
      }
      return api.getSubscription(forId).then(
        (next) => {
          if (idRef.current !== forId || seq.current.get(forId) !== token) return;
          setData(next);
          setError(null);
          setStatus('ready');
        },
        (err: unknown) => {
          if (idRef.current !== forId || seq.current.get(forId) !== token) return;
          if (quiet) throw err;
          setData(null);
          setError(toApiError(err));
          setStatus('error');
        },
      );
    },
    [api],
  );

  const reload = useCallback(() => {
    if (id === undefined) {
      setData(null);
      setError(null);
      setStatus('idle');
      return;
    }
    void load(id, false);
  }, [id, load]);

  const refresh = useCallback(async () => {
    if (id === undefined) return;
    await load(id, true);
  }, [id, load]);

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { status, data, error, reload, refresh };
}
